import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import {
  makeStyles,
  Paper,
  InputBase,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Switch,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  TextRotateUp,
  TextRotationDown,
  ChatBubbleOutline as OpenTicketsIcon,
  Schedule as PendingTicketsIcon,
  CheckCircleOutline as ClosedTicketsIcon,
  GroupOutlined as GroupTicketsIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
} from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ToggleButton from "@material-ui/lab/ToggleButton";

import { FilterAltOff, FilterAlt, PlaylistAddCheckOutlined } from "@mui/icons-material";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { StatusFilter } from "../StatusFilter";
import { WhatsappsFilter } from "../WhatsappsFilter";
import { Button, Snackbar } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import { getTokens } from "../../styles/tokens";

import api from "../../services/api";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

/* Apresentação (F4 — ltenetfast): toolbar única (busca + ações + fila),
 * filtros compactos, abas com sublinhado 2px na marca e contador em tinta.
 * Toda a lógica de filtros/contadores/abas permanece igual. */
const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  // Painel de conversas no padrão do WhatsApp Web: título grande, busca cinza sem borda e
  // filtros de status em pílulas.
  const waDark = theme.palette.type === "dark";
  const wa = waDark
    ? { field: "#202c33", chip: "#202c33", chipText: "#8696a0", chipOn: "#0a332c", chipOnText: "#00a884", focus: "#00a884" }
    : { field: "#f0f2f5", chip: "#f0f2f5", chipText: "#54656f", chipOn: "#d9fdd3", chipOnText: "#008069", focus: "#00a884" };
  return {
    ticketsWrapper: {
      position: "relative",
      display: "flex",
      height: "100%",
      flexDirection: "column",
      overflow: "hidden",
      borderRadius: 0,
      backgroundColor: t.paper,
    },

    /* ---------- toolbar ---------- */
    toolbar: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: "14px 16px 8px",
      backgroundColor: t.paper,
      flex: "none",
    },
    conversationHeader: {
      minHeight: 30,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    conversationTitle: {
      margin: 0,
      color: t.text,
      fontFamily: t.font,
      fontSize: 22,
      fontWeight: 700,
      lineHeight: "28px",
      letterSpacing: "-0.02em",
    },
    // ações do painel no cabeçalho, como os ícones ao lado de "Conversas" no WhatsApp
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      flex: "none",
    },
    headerBtn: {
      width: 36,
      height: 36,
      padding: 0,
      borderRadius: "50%",
      color: wa.chipText,
      "& svg": {
        fontSize: 21,
      },
      "&:hover": {
        backgroundColor: wa.chip,
      },
    },
    headerBtnActive: {
      color: wa.chipOnText,
      backgroundColor: wa.chipOn,
      "&:hover": {
        backgroundColor: wa.chipOn,
      },
    },
    menuCheck: {
      minWidth: 30,
      color: wa.chipOnText,
      "& svg": {
        fontSize: 18,
      },
    },
    menuDanger: {
      color: t.semantic.error,
    },
    filterButton: {
      minWidth: 0,
      height: 30,
      padding: "0 10px",
      borderRadius: 9,
      border: `1px solid ${t.line}`,
      color: t.textSecondary,
      backgroundColor: t.paper,
      textTransform: "none",
      fontSize: 12,
      fontWeight: 600,
      boxShadow: "none",
      "& .MuiButton-startIcon": {
        marginRight: 5,
      },
      "& svg": {
        fontSize: 17,
      },
      "&:hover": {
        color: t.text,
        borderColor: t.accent,
        backgroundColor: t.accentTint.soft,
      },
    },
    filterButtonActive: {
      color: t.accentHover,
      borderColor: t.accent,
      backgroundColor: t.accentTint.soft,
    },
    searchBox: {
      display: "flex",
      alignItems: "center",
      gap: 7,
      height: 38,
      padding: "0 6px 0 14px",
      borderRadius: 19,
      backgroundColor: wa.field,
      border: "1px solid transparent",
      transition: "border-color 150ms ease, background-color 150ms ease",
      "&:focus-within": {
        backgroundColor: t.paper,
        borderColor: wa.focus,
        boxShadow: `0 0 0 1px ${wa.focus}`,
      },
    },
    searchIcon: {
      color: t.textMuted,
      fontSize: 18,
      flex: "none",
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      color: t.text,
      "& input::placeholder": {
        color: t.textMuted,
        opacity: 1,
      },
    },
    searchSwitch: {
      display: "flex",
      alignItems: "center",
      flex: "none",
    },
    actionsRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 32,
    },
    toolBtn: {
      width: 28,
      height: 28,
      padding: 0,
      minWidth: 0,
      border: "none",
      borderRadius: 8,
      color: t.textMuted,
      backgroundColor: "transparent",
      transition: "background-color 150ms ease, color 150ms ease",
      "& svg": {
        fontSize: 17,
      },
      "&:hover": {
        backgroundColor: t.accentTint.soft,
        color: t.text,
      },
      "&.Mui-selected, &.Mui-selected:hover": {
        backgroundColor: t.accentTint.soft,
        color: t.accentHover,
      },
    },
    toolBtnActive: {
      backgroundColor: t.accentTint.soft,
      color: t.accentHover,
      "&:hover": {
        backgroundColor: t.accentTint.medium,
        color: t.accentHover,
      },
    },
    groupToolBtn: {
      position: "relative",
      marginLeft: 2,
    },
    groupCountBadge: {
      position: "absolute",
      top: -5,
      right: -6,
      minWidth: 16,
      height: 16,
      padding: "0 4px",
      borderRadius: 8,
      border: `2px solid ${t.paper}`,
      backgroundColor: t.accent,
      color: t.accentOn,
      fontSize: 9,
      fontWeight: 800,
      lineHeight: "12px",
      fontVariantNumeric: "tabular-nums",
      boxSizing: "content-box",
    },
    queueSelect: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      "& > *": {
        width: "100%",
      },
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
        minHeight: 32,
        borderRadius: 16,
        backgroundColor: wa.field,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
      "& .MuiSelect-root": {
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 14,
        fontSize: 13,
      },
    },

    /* ---------- filtros (Tags / Conexão / Status / Usuário) ---------- */
    filtersPanel: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 6,
      padding: "8px 12px",
      backgroundColor: t.muted,
      borderBottom: `1px solid ${t.line}`,
      "& > .MuiBox-root": {
        padding: "0 !important",
        minWidth: 0,
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: t.paper,
        fontSize: 13,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: t.line,
      },
      "& .MuiAutocomplete-inputRoot": {
        padding: "2px 30px 2px 6px !important",
      },
      "& .MuiAutocomplete-input": {
        padding: "4px 4px !important",
        fontSize: 13,
      },
      "& .MuiChip-root": {
        height: 20,
        fontSize: 11,
        fontWeight: 500,
        borderRadius: 10,
        margin: 1,
      },
      "& .MuiChip-deleteIcon": {
        width: 14,
        height: 14,
      },
      "& .MuiChip-label": {
        paddingLeft: 8,
        paddingRight: 6,
      },
    },

    /* ---------- navegação única por status ---------- */
    // status em pílulas (como "Tudo · Não lidas · Grupos" do WhatsApp), com a contagem ao lado
    // a fileira inteira rola de lado quando não cabe (painel estreito), sem barra aparente
    statusRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flex: "none",
      padding: "2px 16px 10px",
      borderBottom: `1px solid ${t.line}`,
      backgroundColor: t.paper,
      overflowX: "auto",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    tabs: {
      minHeight: 32,
      flex: "none",
      overflow: "visible",
      "& .MuiTabs-scroller, & .MuiTabs-fixed": {
        overflow: "visible !important",
      },
      "& .MuiTabs-flexContainer": {
        gap: 8,
      },
    },
    // "Grupos" é um modo (liga/desliga), não um status: pílula à parte, no fim da fileira
    groupPill: {
      flex: "none",
      height: 32,
      padding: "0 13px",
      border: 0,
      borderRadius: 16,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 14,
      color: wa.chipText,
      backgroundColor: wa.chip,
      transition: "color 150ms ease, background-color 150ms ease",
      "&:hover": {
        filter: waDark ? "brightness(1.15)" : "brightness(0.97)",
      },
      "&:focus-visible": {
        outline: `2px solid ${wa.focus}`,
        outlineOffset: 1,
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: 13,
        padding: "0 10px",
      },
    },
    groupPillOn: {
      color: wa.chipOnText,
      backgroundColor: wa.chipOn,
      fontWeight: 500,
    },
    tabIndicator: {
      display: "none",
    },
    tab: {
      flex: "none",
      minHeight: 32,
      maxHeight: 32,
      minWidth: 0,
      maxWidth: "none",
      padding: "0 13px",
      borderRadius: 16,
      textTransform: "none",
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: 0,
      color: wa.chipText,
      backgroundColor: wa.chip,
      opacity: 1,
      transition: "color 150ms ease, background-color 150ms ease",
      "&:hover": {
        filter: waDark ? "brightness(1.15)" : "brightness(0.97)",
      },
      "&.Mui-selected": {
        color: wa.chipOnText,
        backgroundColor: wa.chipOn,
        fontWeight: 500,
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: 13,
        padding: "0 10px",
      },
    },
    tabLabel: {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      whiteSpace: "nowrap",
    },
    statusIconWrap: {
      order: 2,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": {
        display: "none",
      },
    },
    statusText: {
      order: 1,
      display: "block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "inherit",
      lineHeight: 1,
    },
    count: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12.5,
      fontWeight: 600,
      lineHeight: 1,
      color: "inherit",
      opacity: 0.85,
      fontVariantNumeric: "tabular-nums",
    },
    listsWrapper: {
      position: "relative",
      display: "flex",
      height: "100%",
      flexDirection: "column",
      overflow: "hidden",
      borderRadius: 0,
      backgroundColor: t.paper,
    },

    /* ---------- snackbar "fechar todos" ---------- */
    snackbar: {
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: t.paper,
      color: t.text,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      boxShadow: t.shadow.lift,
      fontSize: 14,
    },
    yesButton: {
      color: t.semantic.error,
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 8,
      marginRight: theme.spacing(0.5),
      "&:hover": {
        backgroundColor: t.muted,
      },
    },
    noButton: {
      color: t.textSecondary,
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 8,
      "&:hover": {
        backgroundColor: t.muted,
      },
    },
  };
});

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [sortTickets, setSortTickets] = useState(false);

  const searchInputRef = useRef();
  const [searchOnMessages, setSearchOnMessages] = useState(false);

  const { user } = useContext(AuthContext);
  const { profile } = user;
  const { setSelectedQueuesMessage } = useContext(QueueSelectedContext);
  const { tabOpen, setTabOpen } = useContext(TicketsContext);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [closedCount, setClosedCount] = useState(0);
  const [panelMenuAnchor, setPanelMenuAnchor] = useState(null);
  const [groupingCount, setGroupingCount] = useState(0);
  // Modo Grupos: as mesmas abas (Atendendo / Aguardando / Resolvidos) passam a listar só grupos.
  const [groupMode, setGroupMode] = useState(false);

  // Outros componentes ainda pedem `setTabOpen("group")` ao aceitar um grupo: vira modo Grupos + Atendendo.
  useEffect(() => {
    if (tabOpen === "group") {
      setGroupMode(true);
      setTab("open");
      setTabOpen("open");
    }
  }, [tabOpen, setTabOpen]);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  // Contagens que não dependem de uma lista montada na tela:
  //  - resolvidos: a lista só é montada ao clicar, então a pílula ficava em 0 até lá;
  //  - grupos: vinha de uma lista escondida que terminava vazia (pílula sempre em 0).
  // O total vem do servidor; refaz quando muda filtro, quando entra/sai ticket e a cada minuto.
  const [countsTick, setCountsTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setCountsTick((n) => n + 1), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = (params, apply) =>
      api
        .get("/tickets", {
          params: {
            pageNumber: 1,
            showAll: showAllTickets,
            queueIds: JSON.stringify(selectedQueueIds),
            ...params,
          },
        })
        .then(({ data }) => {
          if (!cancelled) apply(Number(data?.count) || 0);
        })
        .catch(() => {});
    const timer = setTimeout(() => {
      if (tab !== "closed") fetchCount({ status: "closed", isGroup: String(groupMode) }, setClosedCount);
      if (user.allowGroup) fetchCount({ status: "group" }, setGroupingCount);
    }, 900);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, showAllTickets, selectedQueueIds, groupMode, openCount, pendingCount, countsTick, user.allowGroup]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [filter, setFilter] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    setSelectedQueuesMessage(selectedQueueIds);
  }, [selectedQueueIds]);

  // "Mostrar todos": admin (ou allUserChat) nasce ligado e a escolha fica guardada por usuário —
  // antes vinha sempre desligado e o gestor precisava clicar no olhinho a cada abertura.
  const showAllStorageKey = `digiteai:showAll:${user?.id || "anon"}`;
  useEffect(() => {
    const podeVerTodos =
      String(user?.profile || "").toUpperCase() === "ADMIN" ||
      String(user?.allUserChat || "").toUpperCase() === "ENABLED";
    if (!podeVerTodos) {
      setShowAllTickets(false);
      return;
    }
    let saved = null;
    try {
      saved = localStorage.getItem(showAllStorageKey);
    } catch (e) {
      saved = null;
    }
    setShowAllTickets(saved === null ? true : saved === "true");
  }, [user?.id, user?.profile, user?.allUserChat, showAllStorageKey]);

  const toggleShowAllTickets = () => {
    setShowAllTickets((prevState) => {
      const next = !prevState;
      try {
        localStorage.setItem(showAllStorageKey, String(next));
      } catch (e) {
        // sem storage (modo privado): só não lembra
      }
      return next;
    });
  };

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
    setForceSearch(!forceSearch);
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setForceSearch(!forceSearch);
      setTab("open");
      return;
    } else if (tab !== "search") {
      handleFilter();
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleBack = () => {
    history.push("/tickets");
  };

  const handleChangeStatus = (e, newValue) => {
    handleBack();
    if (newValue === "closed") {
      setTab("closed");
      return;
    }

    setTab("open");
    setTabOpen(newValue);
  };

  const handleChangeGroup = () => {
    handleBack();
    setTab("open");
    setTabOpen("open");
    setGroupMode((current) => !current);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleSnackbarOpen = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const CloseAllTicket = async () => {
    try {
      const { data } = await api.post("/tickets/closeAll", {
        status: tabOpen,
        selectedQueueIds,
      });
      handleSnackbarClose();
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    if (tags.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSelectedTags(tags);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    if (users.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedUsers(users);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    if (whatsapp.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedWhatsapp(whatsapp);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);

    clearTimeout(searchTimeout);

    if (statusFilter.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSelectedStatus(statusFilter);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleFilter = () => {
    if (filter) {
      setFilter(false);
      setTab("open");
      setSearchParam("");
      setSelectedTags([]);
      setSelectedUsers([]);
      setSelectedWhatsapp([]);
      setSelectedStatus([]);
      setForceSearch(!forceSearch);
      if (searchInputRef.current) {
        searchInputRef.current.value = "";
      }
    } else {
      setFilter(true);
      setTab("search");
    }
  };

  const [open, setOpen] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);

  const handleVisibility = () => {
    setHidden((prevHidden) => !prevHidden);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClosed = () => {
    setOpen(false);
  };

  const tooltipTitleStyle = {
    fontSize: "10px",
  };

  const activeStatus =
    tab === "closed"
      ? "closed"
      : tab === "open" && tabOpen === "pending"
      ? "pending"
      : tab === "open"
      ? "open"
      : false;

  const formatCount = (value) => {
    const count = Math.max(0, Number(value) || 0);
    return count > 99 ? "99+" : count;
  };

  return (
    <Paper elevation={0} className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      <div className={classes.toolbar}>
        <div className={classes.conversationHeader}>
          <h2 className={classes.conversationTitle}>Conversas</h2>
          <div className={classes.headerActions}>
            <Can
              role={user.allUserChat === 'enabled' && user.profile === 'user' ? 'admin' : user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <Tooltip title={i18n.t("tickets.buttons.showAll")}>
                  <IconButton
                    aria-pressed={showAllTickets}
                    className={clsx(classes.headerBtn, showAllTickets && classes.headerBtnActive)}
                    onClick={toggleShowAllTickets}
                  >
                    {showAllTickets ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </Tooltip>
              )}
            />
            <Tooltip title={i18n.t("tickets.inbox.newTicket")}>
              <IconButton
                className={classes.headerBtn}
                onClick={() => {
                  setNewTicketModalOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filtrar">
              <IconButton
                aria-pressed={filter}
                className={clsx(classes.headerBtn, filter && classes.headerBtnActive)}
                onClick={handleFilter}
              >
                {filter ? <FilterAlt /> : <FilterAltOff />}
              </IconButton>
            </Tooltip>
            {tab !== "closed" && tab !== "search" && (
              <Tooltip title="Mais opções">
                <IconButton
                  className={classes.headerBtn}
                  aria-haspopup="true"
                  onClick={(e) => setPanelMenuAnchor(e.currentTarget)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
        <Menu
          anchorEl={panelMenuAnchor}
          open={Boolean(panelMenuAnchor)}
          onClose={() => setPanelMenuAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setSortTickets(false);
              setPanelMenuAnchor(null);
            }}
          >
            <ListItemIcon className={classes.menuCheck}>{!sortTickets ? <CheckIcon /> : null}</ListItemIcon>
            Mais recentes primeiro
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSortTickets(true);
              setPanelMenuAnchor(null);
            }}
          >
            <ListItemIcon className={classes.menuCheck}>{sortTickets ? <CheckIcon /> : null}</ListItemIcon>
            Mais antigos primeiro
          </MenuItem>
          {user.profile === "admin" && (
            <MenuItem
              className={classes.menuDanger}
              onClick={() => {
                setPanelMenuAnchor(null);
                handleSnackbarOpen();
              }}
            >
              <ListItemIcon className={clsx(classes.menuCheck, classes.menuDanger)}>
                <PlaylistAddCheckOutlined />
              </ListItemIcon>
              {i18n.t("tickets.inbox.closedAll")}
            </MenuItem>
          )}
        </Menu>

        {/* busca */}
        <div className={classes.searchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={i18n.t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
          />
          <Tooltip placement="top" title="Marque para pesquisar também nos conteúdos das mensagens (mais lento)">
            <div className={classes.searchSwitch}>
              <Switch
                size="small"
                color="primary"
                checked={searchOnMessages}
                onChange={(e) => { setSearchOnMessages(e.target.checked) }}
              />
            </div>
          </Tooltip>
        </div>

        {/* fila (os demais botões foram para o cabeçalho e para a fileira de status) */}
        <div className={classes.actionsRow}>
          <Snackbar
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            message={i18n.t("tickets.inbox.closedAllTickets")}
            ContentProps={{
              className: classes.snackbar,
            }}
            action={
              <>
                <Button
                  className={classes.yesButton}
                  size="small"
                  onClick={CloseAllTicket}
                >
                  {i18n.t("tickets.inbox.yes")}
                </Button>
                <Button
                  className={classes.noButton}
                  size="small"
                  onClick={handleSnackbarClose}
                >
                  {i18n.t("tickets.inbox.no")}
                </Button>
              </>
            }
          />
          <div className={classes.queueSelect}>
            <TicketsQueueSelect
              selectedQueueIds={selectedQueueIds}
              userQueues={user?.queues}
              onChange={(values) => setSelectedQueueIds(values)}
            />
          </div>
        </div>
      </div>

      {filter === true && (
        <div className={classes.filtersPanel}>
          <TagsFilter onFiltered={handleSelectedTags} />
          <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
          <StatusFilter onFiltered={handleSelectedStatus} />
          {profile === "admin" && (
            <UsersFilter onFiltered={handleSelectedUsers} />
          )}
        </div>
      )}

      <div className={classes.statusRow}>
      <Tabs
        value={activeStatus}
        onChange={handleChangeStatus}
        TabIndicatorProps={{ className: classes.tabIndicator }}
        className={classes.tabs}
        aria-label="Status das conversas"
      >
        <Tab
          label={
            <span className={classes.tabLabel}>
              <span className={classes.statusIconWrap}>
                <OpenTicketsIcon />
                <span className={classes.count}>{formatCount(openCount)}</span>
              </span>
              <span className={classes.statusText}>{i18n.t("tickets.inbox.open")}</span>
            </span>
          }
          value="open"
          classes={{ root: classes.tab }}
        />
        <Tab
          label={
            <span className={classes.tabLabel}>
              <span className={classes.statusIconWrap}>
                <PendingTicketsIcon />
                <span className={classes.count}>{formatCount(pendingCount)}</span>
              </span>
              <span className={classes.statusText}>{i18n.t("ticketsList.pendingHeader")}</span>
            </span>
          }
          value="pending"
          classes={{ root: classes.tab }}
        />
        <Tab
          label={
            <span className={classes.tabLabel}>
              <span className={classes.statusIconWrap}>
                <ClosedTicketsIcon />
                <span className={classes.count}>{formatCount(closedCount)}</span>
              </span>
              <span className={classes.statusText}>{i18n.t("tickets.inbox.resolverd")}</span>
            </span>
          }
          value="closed"
          classes={{ root: classes.tab }}
        />
      </Tabs>
      {user.allowGroup && (
        <button
          type="button"
          aria-pressed={groupMode}
          title={i18n.t("ticketsList.groupingHeader")}
          className={clsx(classes.groupPill, groupMode && classes.groupPillOn)}
          onClick={handleChangeGroup}
        >
          {i18n.t("ticketsList.groupingHeader")}
          <span className={classes.count}>{formatCount(groupingCount)}</span>
        </button>
      )}
      </div>

      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>

        <Paper elevation={0} className={classes.listsWrapper}>
          <TicketsList
            key={`open-${groupMode ? "group" : "chat"}`}
            status="open"
            isGroup={groupMode}
            showAll={showAllTickets}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
            setTabOpen={setTabOpen}
          />
          <TicketsList
            key={`pending-${groupMode ? "group" : "chat"}`}
            status="pending"
            isGroup={groupMode}
            selectedQueueIds={selectedQueueIds}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            showAll={user.profile === "admin" || user.allUserChat === 'enabled' ? showAllTickets : false}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            setTabOpen={setTabOpen}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          key={`closed-${groupMode ? "group" : "chat"}`}
          status="closed"
          isGroup={groupMode}
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
          updateCount={(val) => setClosedCount(val)}
          setTabOpen={setTabOpen}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        {profile === "admin" && (
          <>
            <TicketsList
              statusFilter={selectedStatus}
              searchParam={searchParam}
              showAll={showAllTickets}
              tags={selectedTags}
              users={selectedUsers}
              selectedQueueIds={selectedQueueIds}
              whatsappIds={selectedWhatsapp}
              forceSearch={forceSearch}
              searchOnMessages={searchOnMessages}
              status="search"
            />
          </>
        )}

        {profile === "user" && (
          <TicketsList
            statusFilter={selectedStatus}
            searchParam={searchParam}
            showAll={false}
            tags={selectedTags}
            selectedQueueIds={selectedQueueIds}
            whatsappIds={selectedWhatsapp}
            forceSearch={forceSearch}
            searchOnMessages={searchOnMessages}
            status="search"
          />
        )}
      </TabPanel>
    </Paper >
  );
};

export default TicketsManagerTabs;
